import type { IUserTableFilters } from 'src/types/user';
import type { UseSetStateReturn } from 'minimal-shared/hooks';

import { useBoolean, usePopover } from 'minimal-shared/hooks';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { useTranslate } from 'src/locales';

import { getLockedColumns, areAllColumnsVisible } from '../utils/table-utils';

import type { TableHeadCellProps } from '../components/head/table-head-custom';

// ----------------------------------------------------------------------

type UseTableToolbarLogicProps = {
  filters: UseSetStateReturn<IUserTableFilters>;
  onResetPage: () => void;
  columns?: TableHeadCellProps[]; // For backward compatibility
  allColumns?: TableHeadCellProps[]; // All columns including hidden ones
  visibleColumns?: string[];
  setVisibleColumns?: React.Dispatch<React.SetStateAction<string[]>>;
  onFileSelect?: (data: { file: File; content: string }) => void;
  onExportContact?: () => void;
};

// ----------------------------------------------------------------------

export function useTableToolbarLogic({
  filters,
  onResetPage,
  columns = [],
  allColumns, // All columns for the dropdown
  visibleColumns: externalVisibleColumns,
  setVisibleColumns: externalSetVisibleColumns,
  onFileSelect,
  onExportContact,
}: UseTableToolbarLogicProps) {
  const { t } = useTranslate('translation');
  const menuActions = usePopover();
  const columnPopover = usePopover();
  const importModal = useBoolean();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { state: currentFilters, setState: updateFilters } = filters;

  // Search state with debouncing
  const [searchTerm, setSearchTerm] = useState(currentFilters.name || '');

  // Keep internal searchTerm in sync if currentFilters.name changes externally (e.g. clear filters)
  useEffect(() => {
    if (currentFilters.name !== undefined && currentFilters.name !== searchTerm) {
      setSearchTerm(currentFilters.name);
    }
  }, [currentFilters.name]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce search term update to parent filter (800ms)
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return () => {};
    }
    const timeout = setTimeout(() => {
      const trimmedSearch = searchTerm.trim();
      const trimmedCurrent = (currentFilters.name || '').trim();
      if (trimmedSearch !== trimmedCurrent || (searchTerm === '' && currentFilters.name !== '')) {
        onResetPage();
        updateFilters({ name: trimmedSearch });
      }
    }, 800);
    return () => clearTimeout(timeout);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Column visibility state
  const [internalVisibleColumns, setInternalVisibleColumns] = useState<string[]>(
    columns.map((col) => col.id)
  );
  const visibleColumns = externalVisibleColumns || internalVisibleColumns;
  const setVisibleColumns = externalSetVisibleColumns || setInternalVisibleColumns;

  // Column search state
  const [columnSearch, setColumnSearch] = useState('');

  // Handlers
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleSearchKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        const trimmedSearch = searchTerm.trim();
        const trimmedCurrent = (currentFilters.name || '').trim();
        if (trimmedSearch !== trimmedCurrent || (searchTerm === '' && currentFilters.name !== '')) {
          onResetPage();
          updateFilters({ name: trimmedSearch });
        }
      }
    },
    [searchTerm, currentFilters.name, onResetPage, updateFilters]
  );

  const handleImportClick = () => importModal.onTrue();

  const handleFileChange = (file: File) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const fileContent = reader.result;
      if (fileContent && typeof fileContent === 'string') {
        onFileSelect?.({ file, content: fileContent });
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.readAsText(file);
  };

  const handleExport = () => {
    menuActions.onClose();
    onExportContact?.();
  };

  // Column visibility logic - use allColumns if provided, otherwise fall back to columns
  const columnsForDropdown = allColumns || columns;

  const filteredColumns = useMemo(() => {
    if (!columnSearch.trim()) return columnsForDropdown;
    return columnsForDropdown.filter((col: any) =>
      col.label.toLowerCase().includes(columnSearch.toLowerCase())
    );
  }, [columnsForDropdown, columnSearch]);

  const lockedColumns = getLockedColumns(columnsForDropdown);
  const allVisible = areAllColumnsVisible(columnsForDropdown, visibleColumns, lockedColumns);

  const handleToggleAll = () => {
    if (allVisible) {
      // Hide all except locked columns
      setVisibleColumns([...lockedColumns]);
    } else {
      // Show all columns
      setVisibleColumns(columnsForDropdown.map((col) => col.id));
    }
  };

  const handleResetColumns = () => {
    const reset = Array.from(
      new Set([...columnsForDropdown.map((col) => col.id), ...lockedColumns])
    );
    setColumnSearch('');
    setVisibleColumns(reset);
  };

  const handleToggleColumn = (colId: string) => {
    const isLocked = lockedColumns.includes(colId);
    if (isLocked) return; // cannot hide locked column

    setVisibleColumns((prev) =>
      prev.includes(colId) ? prev.filter((c) => c !== colId) : [...prev, colId]
    );
  };

  // Return grouped props
  return {
    // Search props
    searchProps: {
      searchTerm,
      handleSearchChange,
      handleSearchKeyDown,
    },

    // Column visibility props
    columnProps: {
      visibleColumns,
      filteredColumns,
      columnSearch,
      setColumnSearch,
      lockedColumns,
      allVisible,
      handleToggleColumn,
      handleToggleAll,
      handleResetColumns,
    },

    // Import/Export props
    importExportProps: {
      fileInputRef,
      handleImportClick,
      handleFileChange,
      handleExport,
    },

    // Popovers
    popovers: {
      menuActions,
      columnPopover,
      importModal,
    },

    // Translation
    t,
  };
}
